import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '@environments/environment';
import {
  IFBCompany,
  IFBCompanyFeatureConf,
  IFBCompanyFeatureFlags,
  IFBCompanyInvitationsPage,
  IFBCompanyLanguage,
  IFBCompanyRegistrationPageBranding,
  IFBCompanyRegistrationPageBrandingUpdateBody,
  IFBMetadataInfo,
  IFBRegistrationLink,
  IFBMetadataComponent,
  IFBCompanySettings
} from '@interfaces';
import { CompanyService } from '@app/shared/services/company.service';
import { IFBInvitationsQuery } from '@app/models/invitations';
import { IFBRegistrationLinkSource } from '@app/models/registration';

export interface IFBCompanySegmentation {
  config: [
    {
      enabled: boolean;
      propertyId: string;
    }
  ];
}

export const companyResourceStub = {
  getCompany: () => {},
  getRegistrationPageBranding: () => {},
  getCompanyFeatures: () => {},
  getCompanyFeaturesConf: () => {},
  getMetadata: () => {},
  getMetadataConf: () => {},
  updateMetadataComponent: () => {},
  setCompanyFeatures: () => {},
  getCompanyLanguages: () => {},
  getInvitations: () => {},
  update: () => {},
  updateCompanySettings: () => {},
  updateRegistrationPageBranding: () => {},
  atsForwardingStatus: () => {},
  setFeatures: () => {},
  getRegistrationLink: () => {},
  updateRegistrationLink: () => {},
  getRewardPlanSegments: () => of('companyId')
};

@Injectable({
  providedIn: 'root'
})
export class CompanyResource {
  private propertyId: string;

  constructor(
    private companyService: CompanyService,
    private http: HttpClient
  ) {}

  getCompany(companyIdOrSubdomain: string) {
    return this.http.get<IFBCompany>(this.url(companyIdOrSubdomain));
  }

  getRegistrationPageBranding() {
    if (!this.companyId) {
      return of({} as IFBCompanyRegistrationPageBranding);
    }

    const url = `${this.url(this.companyId)}/registration_page`;
    return this.http.get<IFBCompanyRegistrationPageBranding>(url);
  }

  getCompanyFeatures(companyId?: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.url(companyId || this.companyId)}/features`);
  }

  getCompanyFeaturesConf(companyId?: string): Observable<IFBCompanyFeatureConf[]> {
    return this.getCompanyFeatures(companyId).pipe(
      map(features => {
        return features.map(feature => {
          const key = 'CompanyFeature.' + feature;
          return {
            key,
            active: true,
            name: key,
            description: key
          };
        });
      })
    );
  }

  getMetadata(host: string = this.companyService.x1brdHost) {
    return this.http.get<IFBMetadataInfo<IFBMetadataComponent>>(
      `https://webapp.${environment.SERVICES_BASE}/talentscout/api/metadata`,
      {
        headers: {
          Accept: 'application/json',
          'X-1brd-Host': host
        }
      }
    );
  }

  getMetadataConf(): Observable<IFBMetadataInfo<IFBCompanyFeatureConf>> {
    return this.getMetadata().pipe(
      map(response => {
        return {
          ...response,
          components: response.components.map(component => {
            const key = 'CompanyFeature.' + component.name;
            return {
              key,
              active: component.status === 'ENABLED',
              name: key,
              description: key
            };
          })
        } as unknown as IFBMetadataInfo<IFBCompanyFeatureConf>;
      })
    );
  }

  updateMetadataComponent(componentName: string, status: 'ENABLED' | 'DISABLED') {
    return this.http.put<IFBMetadataComponent>(
      `https://webapp.${environment.SERVICES_BASE}/api/applications/${this.companyId}/modules/talentscout/components/${componentName}`,
      {
        status
      }
    );
  }

  setCompanyFeatures(features: IFBCompanyFeatureFlags) {
    return this.http.put(`${this.url(this.companyId)}/features`, features);
  }

  getCompanyLanguages(companyId?: string) {
    return this.http.get<IFBCompanyLanguage>(`${this.url(companyId || this.companyId)}/languages`);
  }

  getInvitations(params: IFBInvitationsQuery) {
    return this.http.get<IFBCompanyInvitationsPage>(`${this.url(this.companyId)}/invitations`, { params });
  }

  update(updatedCompanyData: Partial<IFBCompany>) {
    return this.http.put<IFBCompany>(this.url(this.companyId), updatedCompanyData);
  }

  updateCompanySettings(newSettings: Partial<IFBCompanySettings>) {
    return this.http.put<IFBCompanySettings>(
      `https://webapp.${environment.SERVICES_BASE}/api/applications/${this.companyId}/modules/settings`,
      newSettings
    );
  }

  updateRegistrationPageBranding(body: IFBCompanyRegistrationPageBrandingUpdateBody) {
    const url = `${this.url(this.companyId)}/registration_page`;
    return this.http.put<IFBCompanyRegistrationPageBranding>(url, body);
  }

  atsForwardingStatus() {
    return this.http
      .get<{ forwardingEnabled: boolean }>(`${this.url(this.companyId)}/ats_forwarding`)
      .pipe(catchError(() => of({ forwardingEnabled: false })));
  }

  setFeatures(body: IFBCompanyFeatureFlags) {
    return this.http.put<IFBCompanyFeatureConf[]>(`${this.url(this.companyId)}/features`, body);
  }

  getRegistrationLink() {
    return this.http.get<IFBRegistrationLink>(this.registrationLinkUrl);
  }

  updateRegistrationLink(body: IFBRegistrationLinkSource) {
    return this.http.put<void>(this.registrationLinkUrl, body);
  }

  getRewardPlanSegments(): Observable<string> {
    if (this.propertyId) {
      return of(this.propertyId);
    }

    return this.http.get<IFBCompanySegmentation>(`${this.url(this.companyId)}/rewarding/segmentation`).pipe(
      map(companySegmentation => {
        if (!companySegmentation || !companySegmentation.config || companySegmentation.config.length < 1) {
          return null;
        }

        this.propertyId = companySegmentation.config.find(config => config.enabled).propertyId;
        return this.propertyId;
      })
    );
  }

  private get companyId(): string {
    return this.companyService.data.id;
  }

  private url(companyId: string): string {
    return `https://${environment.API_BASE}/v1/companies/${companyId}`;
  }

  private get registrationLinkUrl(): string {
    return `${this.url(this.companyId)}/registration_link`;
  }
}
